import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { Footer } from "@/components/layout/Footer";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ROUTES, LEGACY_ROUTES } from "@/config/routes";

// Redirect component that preserves URL parameters
const RedirectWithParams = ({ to }: { to: string }) => {
  const params = useParams();
  const id = params.id || params['*'];
  const finalPath = id ? to.replace(':id', id) : to;
  return <Navigate to={finalPath} replace />;
};

// Lazy load route components for better code splitting
const UserDetail = lazy(() => import("./pages/dashboard/UserDetail"));
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Register = lazy(() => import("./pages/Register"));
const RegisterWizard = lazy(() => import("./pages/auth/RegisterWizard"));
const CompanyOnboarding = lazy(() => import("./pages/onboarding/CompanyOnboarding"));
const ProviderOnboarding = lazy(() => import("./pages/onboarding/ProviderOnboarding"));
const CompanyDashboard = lazy(() => import("./pages/dashboard/CompanyDashboard"));
const ProviderDashboard = lazy(() => import("./pages/dashboard/ProviderDashboard"));
const AdminDashboard = lazy(() => import("./pages/dashboard/AdminDashboard"));
const ProviderDetail = lazy(() => import("./pages/dashboard/ProviderDetail"));
const CompanyDetail = lazy(() => import("./pages/dashboard/CompanyDetail"));
const ImportedProviderDetail = lazy(() => import("./pages/dashboard/ImportedProviderDetail"));
const CompanyProviderDetail = lazy(() => import("./pages/dashboard/CompanyProviderDetail"));
const Search = lazy(() => import("./pages/Search"));
const BookingDetail = lazy(() => import("./pages/booking/BookingDetail"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const ForBedrifter = lazy(() => import("./pages/ForBedrifter"));
const ForTilbydere = lazy(() => import("./pages/ForTilbydere"));
const OmOss = lazy(() => import("./pages/OmOss"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const PersonvernOgVilkar = lazy(() => import("./pages/PersonvernOgVilkar"));
const Sikkerhet = lazy(() => import("./pages/Sikkerhet"));
const HvorMyeKanJegSpaere = lazy(() => import("./pages/HvorMyeKanJegSpaere"));
const Notifications = lazy(() => import("./pages/dashboard/Notifications"));
const GenerateTestUsers = lazy(() => import("./pages/GenerateTestUsers"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Minimal loading fallback to avoid layout shift
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <ImpersonationProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <div className="flex flex-col min-h-screen">
            <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Main Routes */}
              <Route path={ROUTES.HOME} element={<Index />} />
              
              {/* Authentication */}
              <Route path={ROUTES.LOGIN} element={<Auth />} />
              <Route path={ROUTES.REGISTER} element={<Register />} />
              <Route path={ROUTES.REGISTER_WIZARD} element={<RegisterWizard />} />
              
              {/* Onboarding */}
              <Route path={ROUTES.ONBOARDING_COMPANY} element={<CompanyOnboarding />} />
              <Route path={ROUTES.ONBOARDING_PROVIDER} element={<ProviderOnboarding />} />
              
              {/* Dashboards */}
              <Route path={ROUTES.COMPANY_DASHBOARD} element={<CompanyDashboard />} />
              <Route path={ROUTES.PROVIDER_DASHBOARD} element={<ProviderDashboard />} />
              <Route path={ROUTES.ADMIN_DASHBOARD} element={<AdminDashboard />} />
              
              {/* Detail Pages */}
              <Route path={`${ROUTES.ADMIN_PROVIDER_DETAIL}/:id`} element={<ProviderDetail />} />
              <Route path={`${ROUTES.IMPORTED_PROVIDER_DETAIL}/:id`} element={<ImportedProviderDetail />} />
              <Route path={`${ROUTES.COMPANY_PROVIDER_DETAIL}/:id`} element={<CompanyProviderDetail />} />
              <Route path={`${ROUTES.ADMIN_COMPANY_DETAIL}/:id`} element={<CompanyDetail />} />
              <Route path={`${ROUTES.ADMIN_DASHBOARD}/brukere/:id`} element={<UserDetail />} />
              
              {/* Notifications */}
              <Route path="/notifications" element={<Notifications />} />
              
              {/* Developer/Testing */}
              <Route path="/dev/generate-test-users" element={<GenerateTestUsers />} />
              
              {/* Search and Booking */}
              <Route path={ROUTES.SEARCH} element={<Search />} />
              <Route path={`${ROUTES.BOOKING}/:id`} element={<BookingDetail />} />
              
              {/* Informational Pages */}
              <Route path={ROUTES.HOW_IT_WORKS} element={<HowItWorks />} />
              <Route path={ROUTES.FOR_COMPANIES} element={<ForBedrifter />} />
              <Route path={ROUTES.FOR_PROVIDERS} element={<ForTilbydere />} />
              <Route path={ROUTES.ABOUT} element={<OmOss />} />
              <Route path={ROUTES.SAVINGS_CALCULATOR} element={<HvorMyeKanJegSpaere />} />
              <Route path={ROUTES.CONTACT} element={<Kontakt />} />
              <Route path={ROUTES.PRIVACY_TERMS} element={<PersonvernOgVilkar />} />
              <Route path={ROUTES.SECURITY} element={<Sikkerhet />} />
              
              {/* Legacy Route Redirects (Backward Compatibility) */}
              <Route path={LEGACY_ROUTES.AUTH} element={<Navigate to={ROUTES.LOGIN} replace />} />
              <Route path={LEGACY_ROUTES.ONBOARDING_COMPANY} element={<Navigate to={ROUTES.ONBOARDING_COMPANY} replace />} />
              <Route path={LEGACY_ROUTES.ONBOARDING_PROVIDER} element={<Navigate to={ROUTES.ONBOARDING_PROVIDER} replace />} />
              <Route path={LEGACY_ROUTES.COMPANY_DASHBOARD} element={<Navigate to={ROUTES.COMPANY_DASHBOARD} replace />} />
              <Route path={LEGACY_ROUTES.PROVIDER_DASHBOARD} element={<Navigate to={ROUTES.PROVIDER_DASHBOARD} replace />} />
              <Route path={LEGACY_ROUTES.ADMIN_DASHBOARD} element={<Navigate to={ROUTES.ADMIN_DASHBOARD} replace />} />
              <Route path={`${LEGACY_ROUTES.ADMIN_PROVIDER_DETAIL}/:id`} element={<RedirectWithParams to={`${ROUTES.ADMIN_PROVIDER_DETAIL}/:id`} />} />
              <Route path={`${LEGACY_ROUTES.ADMIN_COMPANY_DETAIL}/:id`} element={<RedirectWithParams to={`${ROUTES.ADMIN_COMPANY_DETAIL}/:id`} />} />
              <Route path={`${LEGACY_ROUTES.COMPANY_PROVIDER_DETAIL}/:id`} element={<RedirectWithParams to={`${ROUTES.COMPANY_PROVIDER_DETAIL}/:id`} />} />
              <Route path={`${LEGACY_ROUTES.IMPORTED_PROVIDER_DETAIL}/:id`} element={<RedirectWithParams to={`${ROUTES.IMPORTED_PROVIDER_DETAIL}/:id`} />} />
              <Route path={LEGACY_ROUTES.SEARCH} element={<Navigate to={ROUTES.SEARCH} replace />} />
              <Route path={`${LEGACY_ROUTES.BOOKING}/:id`} element={<RedirectWithParams to={`${ROUTES.BOOKING}/:id`} />} />
              
              {/* 404 Not Found */}
              <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
            <Footer />
          </div>
        </BrowserRouter>
      </TooltipProvider>
    </ImpersonationProvider>
  </ThemeProvider>
  </QueryClientProvider>
);

export default App;
